/**
 * ============================================================
 * KEJJA OS — src/pages/pantry/PantryRow.tsx
 * Shared row component used by all pantry views.
 * ============================================================
 */

import { useState } from 'react';
import {
  Minus, PlusCircle, AlertTriangle, Clock, Trash2, Pencil,
} from 'lucide-react';
import { LOCATIONS } from './PantryLayout';
import { useAdjustPantry, useDeletePantryItem } from '@/hooks/usePantry';
import type { PantryItem } from '@/lib/pantryApi';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import PantryItemForm from './PantryItemForm';

interface Props {
  item: PantryItem;
  homeId: string;
}

export default function PantryRow({ item, homeId }: Props) {
  const adjustMut = useAdjustPantry(homeId);
  const deleteMut = useDeletePantryItem(homeId);
  const [editing, setEditing] = useState(false);
  const loc = LOCATIONS.find((l) => l.value === item.location);

  return (
    <>
      <div className={cn(
        'p-3 flex items-center gap-3',
        item.isOutOfStock && 'bg-rose-50/40 dark:bg-rose-950/10',
        item.isLowStock && !item.isOutOfStock && 'bg-amber-50/40 dark:bg-amber-950/10',
      )}>
        <div className="h-9 w-9 rounded-lg bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 flex items-center justify-center flex-shrink-0 text-lg">
          {item.emoji ?? '📦'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm truncate">{item.name}</span>
            {item.isOutOfStock && (
              <Badge variant="outline" className="text-[9px] h-4 px-1 border-rose-500/60 text-rose-700 dark:text-rose-300">
                <AlertTriangle className="w-2 h-2 mr-0.5" /> Out
              </Badge>
            )}
            {item.isLowStock && !item.isOutOfStock && (
              <Badge variant="outline" className="text-[9px] h-4 px-1 border-amber-500/60 text-amber-700 dark:text-amber-300">
                <AlertTriangle className="w-2 h-2 mr-0.5" /> Low
              </Badge>
            )}
            {item.isExpiringSoon && item.daysUntilExpiry !== null && (
              <Badge variant="outline" className={cn(
                'text-[9px] h-4 px-1',
                item.daysUntilExpiry < 0
                  ? 'border-rose-500/60 text-rose-700 dark:text-rose-300'
                  : 'border-orange-500/60 text-orange-700 dark:text-orange-300',
              )}>
                <Clock className="w-2 h-2 mr-0.5" />
                {item.daysUntilExpiry < 0
                  ? `Expired ${Math.abs(item.daysUntilExpiry)}d ago`
                  : item.daysUntilExpiry === 0 ? 'Expires today'
                  : `${item.daysUntilExpiry}d left`}
              </Badge>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="tabular-nums font-semibold">{item.quantity} {item.unit}</span>
            {item.minStockLevel !== null && (
              <span className="opacity-70">· min {item.minStockLevel}</span>
            )}
            {loc && <span>· {loc.emoji} {loc.label}</span>}
          </div>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={() => adjustMut.mutate({ itemId: item.id, delta: -1 })}
            className="h-7 w-7 rounded-md hover:bg-muted text-muted-foreground flex items-center justify-center"
            aria-label="Decrease"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => adjustMut.mutate({ itemId: item.id, delta: 1 })}
            className="h-7 w-7 rounded-md hover:bg-muted text-emerald-700 dark:text-emerald-300 flex items-center justify-center"
            aria-label="Increase"
          >
            <PlusCircle className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setEditing(true)}
            className="h-7 w-7 rounded-md hover:bg-muted text-muted-foreground flex items-center justify-center"
          >
            <Pencil className="w-3 h-3" />
          </button>
          <button
            onClick={() => {
              if (confirm(`Delete "${item.name}"?`)) deleteMut.mutate(item.id);
            }}
            className="h-7 w-7 rounded-md hover:bg-muted text-muted-foreground hover:text-rose-600 flex items-center justify-center"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      {editing && (
        <PantryItemForm
          homeId={homeId}
          item={item}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}
